/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Color Utility Functions
 *
 * Utilities for color manipulation, conversion, and palette generation
 * for dynamic theming based on organization settings.
 */

/**
 * HSL color representation
 */
export type ColorHsl = { h: number; s: number; l: number };

/**
 * Convert hex color to HSL format
 * Returns HSL values as space-separated string (e.g., "210 20% 96%")
 * for use in CSS variables: hsl(var(--primary))
 */
export function hexToHsl(hex: string): string {
  // Remove # if present
  const cleanHex = hex.replace("#", "");

  // Parse RGB
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  h = Math.round(h * 360);
  s = Math.round(s * 100);
  const lPercent = Math.round(l * 100);

  return `${h} ${s}% ${lPercent}%`;
}

/**
 * Convert HSL string to hex color
 * Input format: "210 20% 96%" or { h: 210, s: 20, l: 96 }
 */
export function hslToHex(
  hsl: string | { h: number; s: number; l: number },
): string {
  let h: number;
  let s: number;
  let l: number;

  if (typeof hsl === "string") {
    const parts = hsl.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
    if (!parts) throw new Error(`Invalid HSL format: ${hsl}`);
    h = parseInt(parts[1]!, 10);
    s = parseInt(parts[2]!, 10) / 100;
    l = parseInt(parts[3]!, 10) / 100;
  } else {
    h = hsl.h;
    s = hsl.s / 100;
    l = hsl.l / 100;
  }

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h >= 0 && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (h >= 300 && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Parse HSL string to object
 */
export function parseHsl(hsl: string): { h: number; s: number; l: number } {
  const parts = hsl.match(/(\d+)\s+(\d+)%\s+(\d+)%/);
  if (!parts) throw new Error(`Invalid HSL format: ${hsl}`);
  return {
    h: parseInt(parts[1]!, 10),
    s: parseInt(parts[2]!, 10),
    l: parseInt(parts[3]!, 10),
  };
}

/**
 * Generate a lighter shade of a color
 * @param hsl - HSL string or object
 * @param amount - Amount to lighten (0-100, percentage points)
 */
export function lighten(
  hsl: string | { h: number; s: number; l: number },
  amount: number,
): string {
  const parsed = typeof hsl === "string" ? parseHsl(hsl) : hsl;
  const newL = Math.min(100, Math.max(0, parsed.l + amount));
  return `${parsed.h} ${parsed.s}% ${newL}%`;
}

/**
 * Generate a darker shade of a color
 * @param hsl - HSL string or object
 * @param amount - Amount to darken (0-100, percentage points)
 */
export function darken(
  hsl: string | { h: number; s: number; l: number },
  amount: number,
): string {
  const parsed = typeof hsl === "string" ? parseHsl(hsl) : hsl;
  const newL = Math.max(0, Math.min(100, parsed.l - amount));
  return `${parsed.h} ${parsed.s}% ${newL}%`;
}

/**
 * Adjust saturation of a color
 * @param hsl - HSL string or object
 * @param amount - Amount to adjust saturation (-100 to 100, percentage points)
 */
export function adjustSaturation(
  hsl: string | { h: number; s: number; l: number },
  amount: number,
): string {
  const parsed = typeof hsl === "string" ? parseHsl(hsl) : hsl;
  const newS = Math.min(100, Math.max(0, parsed.s + amount));
  return `${parsed.h} ${newS}% ${parsed.l}%`;
}

/**
 * Generate a desaturated version of a color (for disabled states)
 * @param hsl - HSL string or object
 * @param desaturationPercent - Percentage of saturation to remove (0-100)
 */
export function desaturate(
  hsl: string | { h: number; s: number; l: number },
  desaturationPercent: number = 50,
): string {
  const parsed = typeof hsl === "string" ? parseHsl(hsl) : hsl;
  const reductionFactor = desaturationPercent / 100;
  const newS = Math.max(0, parsed.s * (1 - reductionFactor));
  return `${parsed.h} ${Math.round(newS)}% ${parsed.l}%`;
}

/**
 * Generate a full color scale from an HSL color string
 * Similar to Tailwind's color scales (50, 100, 200, ... 950)
 *
 * The scale uses the input color as the 500 value and generates
 * lighter and darker shades maintaining the hue.
 */
export function generateColorScaleFromHsl(hsl: string): Record<number, string> {
  const parsed = parseHsl(hsl);

  // Generate scale values (in lightness percentages for 500)
  const scale = {
    50: parsed.l + 42, // Very light
    100: parsed.l + 36,
    200: parsed.l + 28,
    300: parsed.l + 18,
    400: parsed.l + 8,
    500: parsed.l, // Base color
    600: parsed.l - 8,
    700: parsed.l - 18,
    800: parsed.l - 28,
    900: parsed.l - 36,
    950: parsed.l - 42, // Very dark
  };

  // For very light or very dark colors, adjust saturation
  const adjustSat = (l: number, s: number): number => {
    // Desaturate very light and very dark colors for better contrast
    if (l > 85) return Math.max(5, s * 0.4);
    if (l < 15) return Math.max(5, s * 0.6);
    return s;
  };

  const result: Record<number, string> = {};

  for (const [key, lightness] of Object.entries(scale)) {
    const l = Math.max(0, Math.min(100, lightness));
    const s = adjustSat(l, parsed.s);
    result[Number(key)] = `${parsed.h} ${Math.round(s)}% ${Math.round(l)}%`;
  }

  return result;
}

/**
 * Generate a full color scale from a primary color (hex format)
 * Similar to Tailwind's color scales (50, 100, 200, ... 950)
 *
 * The scale uses the primary color as the 500 value and generates
 * lighter and darker shades maintaining the hue.
 */
export function generateColorScale(primaryHex: string): Record<number, string> {
  const primaryHsl = hexToHsl(primaryHex);
  return generateColorScaleFromHsl(primaryHsl);
}

/**
 * Calculate contrast ratio between two colors
 * Returns a value from 1 (no contrast) to 21 (maximum contrast)
 * WCAG AA requires 4.5:1 for normal text, 3:1 for large text
 */
export function getContrastRatio(
  color1: string | { h: number; s: number; l: number },
  color2: string | { h: number; s: number; l: number },
): number {
  const getLuminance = (hsl: { h: number; s: number; l: number }): number => {
    // Convert HSL to RGB
    const h = hsl.h / 360;
    const s = hsl.s / 100;
    const l = hsl.l / 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
    const m = l - c / 2;

    let r = 0;
    let g = 0;
    let b = 0;

    if (h < 1 / 6) {
      r = c;
      g = x;
    } else if (h < 2 / 6) {
      r = x;
      g = c;
    } else if (h < 3 / 6) {
      g = c;
      b = x;
    } else if (h < 4 / 6) {
      g = x;
      b = c;
    } else if (h < 5 / 6) {
      r = x;
      b = c;
    } else {
      r = c;
      b = x;
    }

    r = r + m;
    g = g + m;
    b = b + m;

    // Convert to linear RGB and calculate relative luminance
    const toLinear = (val: number): number => {
      return val <= 0.03928
        ? val / 12.92
        : Math.pow((val + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  };

  const hsl1 = typeof color1 === "string" ? parseHsl(color1) : color1;
  const hsl2 = typeof color2 === "string" ? parseHsl(color2) : color2;

  const lum1 = getLuminance(hsl1);
  const lum2 = getLuminance(hsl2);

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Ensure text color has sufficient contrast on a background
 * Returns appropriate foreground color (light or dark)
 */
export function getContrastingTextColor(
  backgroundHsl: string | { h: number; s: number; l: number },
): string {
  const bg =
    typeof backgroundHsl === "string" ? parseHsl(backgroundHsl) : backgroundHsl;

  // Light backgrounds need dark text, dark backgrounds need light text
  const lightText = "0 0% 98%"; // Near white
  const darkText = "0 0% 7%"; // Near black

  const contrastLight = getContrastRatio(bg, parseHsl(lightText));
  const contrastDark = getContrastRatio(bg, parseHsl(darkText));

  // Use the color with better contrast
  return contrastDark > contrastLight ? darkText : lightText;
}

/**
 * Validate hex color format
 */
export function isValidHexColor(hex: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
}

/**
 * Normalize hex color (ensures # prefix and 6 digits)
 */
export function normalizeHexColor(hex: string): string {
  const clean = hex.replace("#", "").toUpperCase();
  if (clean.length === 3) {
    // Expand 3-digit hex to 6 digits
    return `#${clean[0]}${clean[0]}${clean[1]}${clean[1]}${clean[2]}${clean[2]}`;
  }
  return `#${clean.padEnd(6, "0").slice(0, 6)}`;
}
